import { findFacilityProcedureGroups } from './find-facility-procedure-groups.service';

import { findProceduresBySlugs } from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  SearchApiFacilityExam,
  SearchApiPartnerProcedureGroup,
  SearchApiPartnerProcedureOption,
  SearchApiProcedure,
} from '@/app/(main)/(pages)/_types/search-api-response.type';

export interface NotOfferedExam {
  id: string;
  name: string;
}

export interface LaboratoryExamsCoverage {
  offered: SearchApiFacilityExam[];
  notOffered: NotOfferedExam[];
  matched: number;
  total: number;
  isFullCoverage: boolean;
}

function offeredOptionByProcedureId(
  groups: SearchApiPartnerProcedureGroup[],
  facilityId: string,
): Map<string, SearchApiPartnerProcedureOption> {
  const offered = new Map<string, SearchApiPartnerProcedureOption>();

  groups.forEach((group) => {
    const option = (group.options ?? []).find((candidate) => candidate.partnerFacilityId === facilityId);

    if (option && !offered.has(group.procedure.id)) {
      offered.set(group.procedure.id, option);
    }
  });

  return offered;
}

function toFacilityExam(
  procedure: SearchApiProcedure,
  option: SearchApiPartnerProcedureOption | undefined,
): SearchApiFacilityExam {
  return {
    id: procedure.id,
    name: procedure.name,
    code: procedure.code,
    tussCode: procedure.tussCode,
    price: option?.price ?? 0,
    promotion: option?.promotion ?? null,
  };
}

export interface GetLaboratoryExamsCoverageParams {
  facilityId: string;
  examSlugs: string[];
}

export async function getLaboratoryExamsCoverage({
  facilityId,
  examSlugs,
}: GetLaboratoryExamsCoverageParams): Promise<LaboratoryExamsCoverage | null> {
  if (!facilityId || examSlugs.length === 0) {
    return null;
  }

  const procedures = await findProceduresBySlugs(examSlugs);

  if (procedures.length === 0) {
    return null;
  }

  const { partnerId, groups } = await findFacilityProcedureGroups({
    facilityId,
    procedureIds: procedures.map((procedure) => procedure.id),
  });

  if (!partnerId) {
    return null;
  }

  const offeredOptions = offeredOptionByProcedureId(groups, facilityId);

  const offered = procedures
    .filter((procedure) => offeredOptions.has(procedure.id))
    .map((procedure) => toFacilityExam(procedure, offeredOptions.get(procedure.id)));

  const notOffered = procedures
    .filter((procedure) => !offeredOptions.has(procedure.id))
    .map((procedure) => ({ id: procedure.id, name: procedure.name }));

  return {
    offered,
    notOffered,
    matched: offered.length,
    total: procedures.length,
    isFullCoverage: notOffered.length === 0,
  };
}
